import {
  NumberInput,
  SubmitButton,
  SwitchInput,
  TextInputBase,
} from "@ameelio/ui";
import { useMutation, useQuery } from "@apollo/client/react";
import { Alert, Stack } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ContentSkeleton } from "../lib/closet.tsx";
import useApolloErrorHandler from "../lib/handleApolloError";
import Screen from "../lib/Screen";
import {
  hasMaxValue,
  hasMinValue,
  isRequired,
  mergeRules,
} from "../lib/validate";
import { GetSystemDocument } from "./GetSystem.generated.ts";
import { UpdateSystemDocument } from "./UpdateSystem.generated.ts";

type FormData = {
  dataRetention: number;
  recordingRetention: number;
  visitorReapprovalDays: number;
  maxPasswordAgeDays: number | null;
  hasCallTranscripts: boolean;
  hasTablets: boolean;
  allowInmatePasswordReset: boolean;
  enableCovertMessageFlagging: boolean;
};

export default function UpdateSystemScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const handleApolloError = useApolloErrorHandler();
  const { t } = useTranslation();

  if (!params.systemId) {
    throw new Error("No systemId provided");
  }

  const { data, error } = useQuery(GetSystemDocument, {
    variables: { systemId: params.systemId },
    fetchPolicy: "cache-and-network",
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm<FormData>({
    mode: "onTouched",
    defaultValues: {
      recordingRetention: data?.system.recordingRetention,
      visitorReapprovalDays: data?.system.visitorReapprovalDays,
      maxPasswordAgeDays: data?.system.maxPasswordAgeDays ?? null,
      hasCallTranscripts: data?.system.hasCallTranscripts,
      hasTablets: data?.system.hasTablets,
      allowInmatePasswordReset: data?.system.allowInmatePasswordReset,
      enableCovertMessageFlagging: data?.system.enableCovertMessageFlagging,
      dataRetention: data?.system.dataRetention ?? undefined,
    },
  });

  const newRecordingRetention = watch("recordingRetention");
  const newDataRetention = watch("dataRetention");

  const [updateSystem] = useMutation(UpdateSystemDocument, {
    onError: (e) => handleApolloError(e),
  });

  const onSubmit = async ({ ...input }: FormData) => {
    if (!data || !data.system || !data.system.code) {
      throw new Error("System data is not loaded or missing code.");
    }
    await updateSystem({
      variables: {
        input: {
          ...input,
          systemId: data.system.id,
        },
      },
      onCompleted: (data) =>
        navigate(`/toolbox/systems/${data.updateSystem.system.id}`),
    });
  };

  // Populate form with fetched system data once available.
  // We can't set default values directly in useForm because the data
  // hasn't loaded yet - we need to fetch it by ID first. Unlike our
  // dialog-based edit forms (where we fetch before opening), this
  // full-page form requires useEffect + reset to handle the async data.
  useEffect(() => {
    if (data?.system) {
      reset({
        recordingRetention: data.system.recordingRetention,
        visitorReapprovalDays: data.system.visitorReapprovalDays,
        maxPasswordAgeDays: data.system.maxPasswordAgeDays ?? null,
        hasCallTranscripts: data.system.hasCallTranscripts,
        hasTablets: data.system.hasTablets,
        allowInmatePasswordReset: data.system.allowInmatePasswordReset,
        enableCovertMessageFlagging: data.system.enableCovertMessageFlagging,
        dataRetention: data.system.dataRetention ?? undefined,
      });
    }
  }, [data, reset]);

  if (error) throw error;
  if (!data) return <ContentSkeleton />;

  return (
    <Screen title="Update system">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <TextInputBase
            value={data.system.name}
            disabled
            label={t("Name")}
            onChange={() => {}}
          />
          <TextInputBase
            label="Code"
            value={data.system.code.toUpperCase()}
            onChange={() => {}}
            disabled
          />
          <NumberInput
            control={control}
            name="recordingRetention"
            label="Recording retention (days)"
            helperText="The number of days we will keep recordings and transcripts."
            rules={mergeRules(
              isRequired("Recording retention is required"),
              hasMinValue(0),
              hasMaxValue(731)
            )}
          />
          {newRecordingRetention !== data?.system.recordingRetention && (
            <Alert severity="warning">
              Caution: Changing the recording retention value will automatically
              delete any recordings older than the new retention period. This
              action cannot be undone!
            </Alert>
          )}
          <NumberInput
            control={control}
            name="dataRetention"
            helperText="The number of days we will keep meeting logs and inactive accounts. Leave blank to retain indefinitely."
            label="Data retention (days)"
            rules={mergeRules(hasMinValue(0), hasMaxValue(5000))}
          />
          {newDataRetention &&
            newDataRetention !== data?.system.dataRetention && (
              <Alert severity="warning">
                Caution: Changing the data retention value will automatically
                delete any data older than the new retention period. This action
                cannot be undone!
              </Alert>
            )}
          <NumberInput
            control={control}
            name="visitorReapprovalDays"
            label="Visitor re-approval window (days)"
            helperText="The number of days since a resident's profile has become inactive after which their previously approved visitors will need to re-request approval."
            rules={mergeRules(
              isRequired("Visitor re-approval window is required"),
              hasMinValue(0)
            )}
          />
          <NumberInput
            control={control}
            name="maxPasswordAgeDays"
            label="Visitor max password age (days)"
            helperText="Visitors with an organization membership in this system must reset their password if it is older than this many days. Leave empty for no limit."
            rules={hasMinValue(1)}
          />
          <SwitchInput
            control={control}
            name="hasCallTranscripts"
            label="Enable call transcripts?"
          />
          <SwitchInput
            control={control}
            name="hasTablets"
            label="Enable tablet management?"
          />
          <SwitchInput
            control={control}
            name="allowInmatePasswordReset"
            label="Enable resident password reset flow?"
          />
          <SwitchInput
            control={control}
            name="enableCovertMessageFlagging"
            label="Enable covert message flagging?"
          />
          <SubmitButton submitting={isSubmitting}>{t("Submit")}</SubmitButton>
        </Stack>
      </form>
    </Screen>
  );
}
